/**
 * Channel Status Mapping Load Policy (WS-3)
 *
 * Triggered when the channel detail view opens.
 * Loads the integration's `settings.orderStatusMapping` rows plus the canonical
 * order statuses (from `SalesOrderStatuses`) used to build the per-row Select.
 */

import { Policy } from '@commandDrivenStateEngine/core/policies';
import { ClientLibraryType } from '@bett3r-dev/teselly-client-library';
import { ChannelsState, StatusMappingRow } from '../state';
import { ChannelsCommand } from '../commands';

export const createChannelStatusMappingLoadPolicy = (
  backend: ClientLibraryType
): Policy<ChannelsState, ChannelsCommand> => ({
  name: 'channel-status-mapping-load',

  shouldReact: ( prev, next ) => {
    // Trigger when detail view opens (mode changes from closed to view)
    return prev.channelDetail.mode.type === 'closed' && next.channelDetail.mode.type === 'view';
  },

  onStateChange: async ({ next, ctx }) => {
    const { dispatch } = ctx;

    if ( next.channelDetail.mode.type !== 'view' ) return;

    const integrationId = next.channelDetail.mode.integrationId;
    const channel = next.salesChannels[integrationId];

    if ( !channel ) return;

    dispatch(
      { type: 'integrations/channels/detail/loading/setStatusMapping', payload: { isLoading: true }},
      { source: 'policy' }
    );

    try {
      const canonicalStatuses = await backend.queries.SalesOrderStatuses();
      // Prefill from the per-instance override when present; otherwise seed from
      // the connector's declared default mapping (stamped onto the integration
      // row). Saving persists whatever is shown as the per-instance override (DB),
      // so editing a default and saving makes it stick.
      const override = channel.settings?.orderStatusMapping ?? [];
      const source = override.length > 0 ? override : ( channel.defaultOrderStatusMapping ?? []);
      const rows: StatusMappingRow[] = source.map(
        ( m ) => ({ channelStatus: m.channelStatus, canonicalStatusId: m.canonicalStatusId })
      );

      // `load` handler also resets `isLoading` to false.
      dispatch(
        {
          type: 'integrations/channels/detail/statusMapping/load',
          payload: { rows, canonicalStatuses: canonicalStatuses ?? []}
        },
        { source: 'api' }
      );
    } catch ( error ) {
      console.error( 'Error loading channel status mapping:', error );
      dispatch(
        { type: 'integrations/channels/detail/loading/setStatusMapping', payload: { isLoading: false }},
        { source: 'policy' }
      );
    }
  }
});
